// ============================================================================
// GumaHUB Authentication Context
// ============================================================================

import React, { createContext, useCallback, useEffect, useState } from 'react';
import type { ModuleId, ModuleAccess, UserCompany } from '@gumahub/shared';
import { authService } from '../services/auth.service.js';
import type { AuthContextType, AuthState } from '../types/index.js';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export const AuthContext = createContext<AuthContextType | null>(null);

export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      if (authService.hasSession()) {
        const result = await authService.getCurrentUser();
        if (result.success && result.user) {
          setState({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          // Try to refresh token
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success && refreshResult.user) {
            setState({
              user: refreshResult.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            setState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    initAuth();

    // Listen for logout events (from token refresh failure)
    const handleLogout = () => {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const result = await authService.login(username, password);

    if (result.success && result.user) {
      setState({
        user: result.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return true;
    }

    setState(prev => ({
      ...prev,
      isLoading: false,
      error: result.error || 'Login failed',
    }));
    return false;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await authService.logout();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const refreshAuth = useCallback(async (): Promise<void> => {
    const result = await authService.refreshToken();
    if (result.success && result.user) {
      setState(prev => ({
        ...prev,
        user: result.user!,
      }));
    }
  }, []);

  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const getModuleAccess = useCallback((moduleId: ModuleId): ModuleAccess | null => {
    if (!state.user?.modules) return null;
    return state.user.modules[moduleId] || null;
  }, [state.user]);

  const hasModuleAccess = useCallback((moduleId: ModuleId): boolean => {
    const access = getModuleAccess(moduleId);
    return access?.access === true;
  }, [getModuleAccess]);

  const hasRole = useCallback((moduleId: ModuleId, role: string): boolean => {
    const access = getModuleAccess(moduleId);
    if (!access?.access) return false;
    return access.roles.includes(role);
  }, [getModuleAccess]);

  const hasPermission = useCallback((moduleId: ModuleId, permission: string): boolean => {
    const access = getModuleAccess(moduleId);
    if (!access?.access) return false;
    // Admin has all permissions
    if (access.isAdmin) return true;
    return access.permissions.includes(permission);
  }, [getModuleAccess]);

  const isModuleAdmin = useCallback((moduleId: ModuleId): boolean => {
    const access = getModuleAccess(moduleId);
    return access?.isAdmin === true;
  }, [getModuleAccess]);

  const getAccessibleModules = useCallback((): ModuleId[] => {
    if (!state.user?.modules) return [];
    return Object.entries(state.user.modules)
      .filter(([_, access]) => access.access)
      .map(([moduleId]) => moduleId as ModuleId);
  }, [state.user]);

  const switchCompany = useCallback(async (clientId: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const result = await authService.switchCompany(clientId);

    if (result.success && result.user) {
      setState({
        user: result.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return true;
    }

    setState(prev => ({
      ...prev,
      isLoading: false,
      error: result.error || 'Failed to switch company',
    }));
    return false;
  }, []);

  const getAvailableCompanies = useCallback((): UserCompany[] => {
    return state.user?.availableCompanies || [];
  }, [state.user]);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshAuth,
    clearError,
    hasModuleAccess,
    hasRole,
    hasPermission,
    isModuleAdmin,
    getModuleAccess,
    getAccessibleModules,
    switchCompany,
    getAvailableCompanies,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
